import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Filter, Search } from 'lucide-react';
import ProductCategorySection from '../components/sections/products/ProductCategory';
import { productCategories, getProductsByCategory } from '../data/products';

const Products: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle category filter change
  const handleFilterChange = (categoryId: string) => {
    setActiveFilter(categoryId);
    
    // Scroll to the category section if a specific category is selected
    if (categoryId !== 'all') {
      setTimeout(() => {
        const element = document.getElementById(categoryId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Scroll to the top of the products page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <>
      <Helmet>
        <title>Izdelki | Oljarna Krajnc</title>
        <meta 
          name="description" 
          content="Odkrijte naše kakovostne izdelke iz bučnih semen - od tradicionalnega bučnega olja do inovativnih specialitet. Tradicija in kakovost od leta 1927."
        />
      </Helmet>
      
      <section className="pt-32 pb-16 bg-[url('/images/products-hero-bg.jpg')] bg-cover bg-center relative">
        <div className="absolute inset-0 bg-primary-700 opacity-70"></div>
        <div className="container-wide relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Naši izdelki
          </h1>
          <div className="w-24 h-1 bg-secondary mx-auto mb-8"></div>
          <p className="text-white text-lg md:text-xl max-w-3xl mx-auto">
            Odkrijte naše kakovostne izdelke iz bučnih semen - od tradicionalnega 
            bučnega olja do inovativnih specialitet.
          </p>
          
          {/* Search and filter section */}
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search input */}
                <div className="flex-grow relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Search size={20} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Išči izdelke..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none"
                  />
                </div>
                
                {/* Category filter */}
                <div className="flex-shrink-0 flex items-center">
                  <Filter size={20} className="text-primary-600 mr-2" />
                  <span className="text-gray-700 mr-2 hidden md:inline">Kategorije:</span>
                  <select
                    value={activeFilter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none"
                  >
                    <option value="all">Vsi izdelki</option>
                    {productCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Category quick links */}
              <div className="mt-4 flex flex-wrap gap-2">
                <CategoryButton 
                  id="all" 
                  name="Vsi izdelki" 
                  isActive={activeFilter === 'all'} 
                  onClick={() => handleFilterChange('all')}
                />
                
                {productCategories.map(category => (
                  <CategoryButton 
                    key={category.id} 
                    id={category.id} 
                    name={category.name} 
                    isActive={activeFilter === category.id}
                    onClick={() => handleFilterChange(category.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Product categories sections */}
      {productCategories.map((category, index) => (
        <ProductCategorySection 
          key={category.id}
          category={category}
          products={getProductsByCategory(category.id)}
          isEven={index % 2 === 0}
        />
      ))}
      
      {/* Ordering section */}
      <section className="py-16 bg-primary-700 text-white">
        <div className="container-wide text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Želite naročiti naše izdelke?
          </h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto">
            Naše izdelke lahko naročite prek spleta, po telefonu ali nas obiščete v naši oljarni, 
            kjer vam bomo z veseljem predstavili naš celoten asortiment.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#" 
              className="bg-secondary hover:bg-secondary-600 text-white px-8 py-3 rounded-md font-medium transition-colors inline-flex items-center justify-center"
            >
              Obiščite spletno trgovino
            </a>
            
            <a 
              href="/kontakt" 
              className="bg-transparent hover:bg-white hover:text-primary-700 text-white border-2 border-white px-8 py-3 rounded-md font-medium transition-colors inline-flex items-center justify-center"
            >
              Kontaktirajte nas
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

// Category button component
const CategoryButton: React.FC<{
  id: string;
  name: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ name, isActive, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${
        isActive 
          ? 'bg-primary-600 text-white' 
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {name}
    </button>
  );
};

export default Products;